import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Paper,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import { DownloadIcon } from "../../components-new/icons";
import { IconContainer, PageTitle } from "../../components-new/common";
import { useFetchTestStepResultQuery } from "../../useQuery";
import { generateTestCaseReportPDF } from "../../utils/generatePDFReport";
import { ExeReportHistoryStatus } from "../../components-new/layout/constants";
import CustomBreadcrumbs from "../../components-new/common/CustomBreadCrums";

const StyledTableCell = styled(TableCell)`
  color: #e0e0e0;
  font-size: 14px;
  font-weight: 600;
  border-bottom: 1px solid #e2e6f3;
  background: #2550d1;
`;

const StyledTableBodyCell = styled(TableCell)`
  padding: 8px 20px;
  border-bottom: 1px solid #eee;
  p {
    color: #2b3539;
    font-size: 16px;
    font-weight: 500;
    padding-right: 15px;
    min-width: 60px;
  }
`;

const StyledInfoBox = styled(Box)`
  padding: 16px 20px;
`;

const StyledTypography = styled(Typography)`
  font-size: 14px;
  font-weight: 600;
  color: #232d3d;
`;

const StyledTitle = styled(Typography)`
  color: #e0e0e0;
  font-size: 14px;
  font-weight: 600;
  padding: 16px 20px;
  border-bottom: 2px solid rgba(226, 230, 243, 0.8);
`;

const StyledInfo = styled(StyledTitle)`
  color: #232d3d;
`;

const StyledInfoLeft = styled(StyledInfo)`
  border-right: 2px solid rgba(226, 230, 243, 0.8);
  flex: 2;
`;

const StyledInfoRight = styled(StyledInfo)`
  flex: 1;
  text-align: center;
`;

const StatusChip = styled(Chip)(({ status }) => ({
  minWidth: "82px",
  padding: "8px 10px",
  borderRadius: "41px",
  backgroundColor:
    status === "PASS"
      ? "#e7fbe4"
      : status === "FAIL"
        ? "#fbe7e7"
        : status === "ABEND"
          ? "#eef4fc"
          : "#D3D3D3",
  color: "#000000",
  fontWeight: 600,
  textTransform: "capitalize",
}));

const TestCaseReport = () => {
  const location = useLocation();
  const componentRef = useRef();
  const [isDownloading, setIsDownloading] = useState(false);

  const queryParams = new URLSearchParams(location.search);
  const tcrid = queryParams.get("tcrid");
  const tcname = queryParams.get("tcname");
  const tcRundate = queryParams.get("tcRundate");

  const { data: testStepData, isLoading, error, refetch } = useFetchTestStepResultQuery(tcrid);

  useEffect(() => {
    if (tcrid) {
      refetch();
    }
  }, [tcrid, refetch]);

  const breadcrumbs = [
    <Typography key="1" color="text.primary">
      Execution Reports
    </Typography>,
    <Typography key="2" color="text.primary">
      {tcname}
    </Typography>,
    <Typography key="3" color="text.primary">
      Detailed Report
    </Typography>,
  ];

  const handleDownloadPDF = () => {
    generateTestCaseReportPDF(
      componentRef,
      tcname,
      tcRundate,
      () => setIsDownloading(true),
      () => setIsDownloading(false),
      (error) => {
        console.error("Error generating PDF:", error);
        setIsDownloading(false);
      }
    );
  };

  const getStatusCounts = () => {
    if (!testStepData || !Array.isArray(testStepData)) return { pass: 0, fail: 0, total: 0 };
    
    const pass = testStepData.filter(step => step.Status === "PASS").length;
    const fail = testStepData.filter(step => step.Status === "FAIL").length;
    const total = testStepData.length;
    
    return { pass, fail, total };
  };

  const getExecutionInfo = () => {
    if (!testStepData || !Array.isArray(testStepData) || testStepData.length === 0) {
      return null;
    }
    
    const firstStep = testStepData[0];
    
    return {
      testCaseName: tcname,
      testCaseId: firstStep.Test_Case_Results_Id,
      executedBy: firstStep.Executed_By,
      executedDate: firstStep.Executed_Date,
      duration: firstStep.Duration,
      browser: firstStep.Page_Name || "N/A",
      environment: firstStep.TestData,
      status: firstStep.Status || "UNKNOWN",
    };
  };

  const executionInfo = getExecutionInfo();
  const statusCounts = getStatusCounts();

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Error loading test case report: {error.message}
        </Alert>
      </Box>
    );
  }

  if (!testStepData || !Array.isArray(testStepData) || testStepData.length === 0) {
    return (
      <Box p={3}>
        <Alert severity="warning">
          No test step data available for this test case.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box mb={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box>
            <PageTitle title={`Test Case Report - ${tcname}`} variant="h4" />
            <CustomBreadcrumbs ariaLabel="breadcrumb">
              {breadcrumbs}
            </CustomBreadcrumbs>
          </Box>
          <IconContainer
            tooltipText={isDownloading ? "Generating PDF..." : "Download PDF"}
            handleClick={handleDownloadPDF}
            disabled={isDownloading}
          >
            <DownloadIcon />
          </IconContainer>
        </Box>
      </Box>

      <Box ref={componentRef}>
        {/* Header Information */}
        <Box
          mt={2}
          display="flex"
          bgcolor="#ffffff"
          borderBottom="2px solid rgba(226, 230, 243, 0.80)"
          sx={{ borderTopRightRadius: "8px", borderTopLeftRadius: "8px" }}
        >
          <StyledInfoBox flex={1}>
            <StyledTypography>Test Case: {executionInfo?.testCaseName}</StyledTypography>
          </StyledInfoBox>
          <StyledInfoBox
            flex={1}
            borderRight="2px solid rgba(226, 230, 243, 0.80)"
            borderLeft="2px solid rgba(226, 230, 243, 0.80)"
          >
            <StyledTypography>Execution Date: {executionInfo?.executedDate}</StyledTypography>
          </StyledInfoBox>
          <StyledInfoBox flex={1}>
            <StyledTypography>Status: {executionInfo?.status}</StyledTypography>
          </StyledInfoBox>
        </Box>

        {/* Execution Information */}
        <Box mt={4} bgcolor="#2550d1" sx={{ borderTopRightRadius: "8px", borderTopLeftRadius: "8px" }}>
          <StyledInfoBox color="#E0E0E0">
            <Typography fontWeight={600}>Execution Information</Typography>
          </StyledInfoBox>
        </Box>

        <Box display="flex" gap={2}>
          <Box
            mt={2}
            flex={1}
            bgcolor="#fff"
            sx={{ borderTopRightRadius: "8px", borderTopLeftRadius: "8px" }}
          >
            <Box display="flex" borderBottom="2px solid rgba(226, 230, 243, 0.80)">
              <StyledInfoLeft>Test Case ID</StyledInfoLeft>
              <StyledInfoRight>{executionInfo?.testCaseId}</StyledInfoRight>
            </Box>
            <Box display="flex" borderBottom="2px solid rgba(226, 230, 243, 0.80)">
              <StyledInfoLeft>Executed By</StyledInfoLeft>
              <StyledInfoRight>{executionInfo?.executedBy}</StyledInfoRight>
            </Box>
            <Box display="flex" borderBottom="2px solid rgba(226, 230, 243, 0.80)">
              <StyledInfoLeft>Browser</StyledInfoLeft>
              <StyledInfoRight>{executionInfo?.browser}</StyledInfoRight>
            </Box>
          </Box>

          <Box
            mt={2}
            flex={1}
            bgcolor="#fff"
            sx={{ borderTopRightRadius: "8px", borderTopLeftRadius: "8px" }}
          >
            <Box display="flex" borderBottom="2px solid rgba(226, 230, 243, 0.80)">
              <StyledInfoLeft>Total Steps</StyledInfoLeft>
              <StyledInfoRight>{statusCounts.total}</StyledInfoRight>
            </Box>
            <Box display="flex" borderBottom="2px solid rgba(226, 230, 243, 0.80)">
              <StyledInfoLeft>Duration</StyledInfoLeft>
              <StyledInfoRight>{executionInfo?.duration}</StyledInfoRight>
            </Box>
            <Box display="flex" borderBottom="2px solid rgba(226, 230, 243, 0.80)">
              <StyledInfoLeft>Environment</StyledInfoLeft>
              <StyledInfoRight>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    maxWidth: 200,
                    wordBreak: "break-word",
                    textAlign: "center",
                    color: "#232d3d",
                    fontSize: "14px",
                    fontWeight: 600
                  }}
                >
                  {executionInfo?.environment}
                </Typography>
              </StyledInfoRight>
            </Box>
          </Box>
        </Box>

        {/* Summary Statistics */}
        <Box mt={4}>
          <Box bgcolor="#2550D1" sx={{ borderTopLeftRadius: "8px", borderTopRightRadius: "8px" }}>
            <Typography
              color="#E0E0E0"
              fontWeight={600}
              padding="16px 20px"
              borderBottom={1}
              borderColor="gray"
            >
              Execution Summary
            </Typography>
          </Box>

          <Box display="flex" bgcolor="#2550D1">
            <StyledTitle sx={{ flex: 1, textAlign: "center" }}>Total Steps</StyledTitle>
            <StyledTitle sx={{ flex: 1, textAlign: "center" }}>Passed</StyledTitle>
            <StyledTitle sx={{ flex: 1, textAlign: "center" }}>Failed</StyledTitle>
            <StyledTitle sx={{ flex: 1, textAlign: "center" }}>Success Rate</StyledTitle>
          </Box>

          <Box display="flex" bgcolor="#ffffff">
            <StyledInfo sx={{ flex: 1, textAlign: "center", color: "#232d3d" }}>{statusCounts.total}</StyledInfo>
            <StyledInfo sx={{ flex: 1, textAlign: "center", color: "#2e7d32" }}>
              {statusCounts.pass}
            </StyledInfo>
            <StyledInfo sx={{ flex: 1, textAlign: "center", color: "#d32f2f" }}>
              {statusCounts.fail}
            </StyledInfo>
            <StyledInfo sx={{ flex: 1, textAlign: "center", color: "#232d3d" }}>
              {statusCounts.total > 0 ? `${((statusCounts.pass / statusCounts.total) * 100).toFixed(1)}%` : "0%"}
            </StyledInfo>
          </Box>
        </Box>

        {/* Test Steps Table */}
        <Box mt={4}>
          <Box bgcolor="#2550D1" sx={{ borderTopLeftRadius: "8px", borderTopRightRadius: "8px" }}>
            <Typography
              color="#E0E0E0"
              fontWeight={600}
              padding="16px 20px"
              borderBottom={1}
              borderColor="gray"
            >
              Test Steps Execution Details
            </Typography>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <StyledTableCell>Step #</StyledTableCell>
                  <StyledTableCell>Keyword</StyledTableCell>
                  <StyledTableCell>Action</StyledTableCell>
                  <StyledTableCell>Test Data</StyledTableCell>
                  <StyledTableCell>Expected Result</StyledTableCell>
                  <StyledTableCell>Status</StyledTableCell>
                  <StyledTableCell>Duration</StyledTableCell>
                  <StyledTableCell>Comments</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {testStepData.map((step, index) => (
                  <TableRow key={index}>
                    <StyledTableBodyCell>{step.Step_Number}</StyledTableBodyCell>
                    <StyledTableBodyCell>{step.Keyword}</StyledTableBodyCell>
                    <StyledTableBodyCell>{step.Action}</StyledTableBodyCell>
                    <StyledTableBodyCell>
                      <Typography variant="body2" sx={{ maxWidth: 200, wordBreak: "break-word" }}>
                        {step.TestData}
                      </Typography>
                    </StyledTableBodyCell>
                    <StyledTableBodyCell>
                      <Typography variant="body2" sx={{ maxWidth: 200, wordBreak: "break-word" }}>
                        {step.Page_Description || "N/A"}
                      </Typography>
                    </StyledTableBodyCell>
                    <StyledTableBodyCell>
                      <Typography
                        variant="body2"
                        sx={{
                          padding: "4px 8px",
                          borderRadius: "4px",
                          backgroundColor:
                            step.Status === "PASS"
                              ? "#e7fbe4"
                              : step.Status === "FAIL"
                                ? "#fbe7e7"
                                : step.Status === "ABEND"
                                  ? "#eef4fc"
                                  : "#D3D3D3",
                          color: "#000000",
                          fontWeight: 600,
                          textTransform: "capitalize",
                          display: "inline-block",
                          minWidth: "60px",
                          textAlign: "center",
                        }}
                      >
                        {ExeReportHistoryStatus[step.Status] || step.Status}
                      </Typography>
                    </StyledTableBodyCell>
                    <StyledTableBodyCell>{step.test_step_duration || "N/A"}</StyledTableBodyCell>
                    <StyledTableBodyCell>
                      <Typography variant="body2" sx={{ maxWidth: 200, wordBreak: "break-word" }}>
                        {step.test_step_issues || "N/A"}
                      </Typography>
                    </StyledTableBodyCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </Box>
  );
};

export default TestCaseReport; 